import React from 'react'
import { graphql } from 'gatsby'
import { BREAKPOINT, COLOR } from '@farewill/ui/tokens'
import { Divider, Wrapper } from '@farewill/ui'

import PATHS from 'paths'
import { TRUSTPILOT_RATING_OUT_OF_5, TRUSTPILOT_REVIEWS_TOTAL } from 'config'
import { PRODUCTS } from 'lib/products/constants'
import { TRACKING_TYPES } from 'lib/tracking/constants'
import { roundDownToNearestHundred } from 'lib/formatting/numbers'
import DefaultLayout from 'layouts/DefaultLayout'
import BreakpointSwitch from 'components/BreakpointSwitch'
import CoBrandedHeader from 'components/Header/Custom/CoBranded'
import GetStarted from 'views/wills/components/Overview/GetStarted'

import Hero from '../slices/Hero'
import WillOptions from '../slices/WillOptions'
import GiftStories from '../slices/GiftStories'
import GiftSlider from '../slices/GiftSlider'
import Trustpilot from '../slices/Trustpilot'
import FAQ from '../slices/FAQ'
import Pledges from '../slices/Pledges'

import type { GreyLabelLandingPageProps } from '../types'

const MacmillanGreyLabelLandingPage = ({
  data: {
    page: {
      utmCampaign,
      utmMedium,
      partner,
      heroTitle,
      heroSubtitle,
      heroCtaText,
      telephoneWillsEnabled,
    },
    faqSection,
  },
}: GreyLabelLandingPageProps): React.ReactElement => {
  const areTelephoneWillsEnabled =
    typeof telephoneWillsEnabled === 'boolean'
      ? telephoneWillsEnabled
      : partner.telephoneWillsEnabled

  return (
    <DefaultLayout
      branding={partner.utmSource}
      title="Online Will Writing Service | Legal Will In 15 Minutes"
      description={`Our solicitor-approved guide helps you write a will online from the comfort of your own home. Rated ${TRUSTPILOT_RATING_OUT_OF_5} out of 5 by over ${roundDownToNearestHundred(
        TRUSTPILOT_REVIEWS_TOTAL
      )} customers on Trustpilot. National Will Writing Firm of the Year.`}
      meta={[
        {
          name: 'robots',
          content: 'noindex',
        },
      ]}
      product={PRODUCTS.WILLS}
      canonicalPath={PATHS.WILLS.OVERVIEW}
      headerColor={COLOR.WHITE}
      customHeader={
        <CoBrandedHeader
          altText="Macmillan Cancer Support"
          assetDirectory="macmillan"
          partnerLogoUrl="https://www.macmillan.org.uk/donate/gifts-in-wills/free-online-will-service"
        />
      }
      footerType="compact"
      utmParameters={{
        partnerId: partner?.partnerId || TRACKING_TYPES.NOT_SET,
        utmCampaign: utmCampaign || TRACKING_TYPES.NOT_SET,
        utmMedium: utmMedium || TRACKING_TYPES.NOT_SET,
        utmSource: partner?.utmSource || TRACKING_TYPES.NOT_SET,
        utmChannel: partner?.utmChannel || TRACKING_TYPES.NOT_SET,
      }}
    >
      <Wrapper background={COLOR.WHITE}>
        <Hero
          assetDirectory="macmillan"
          title={heroTitle || 'Write your will for free today'}
          subtitle={heroSubtitle?.heroSubtitle}
          ctaText={heroCtaText || 'Let’s get started'}
          telephoneWillsEnabled={areTelephoneWillsEnabled}
          imageTransformationParams={{
            small: 'ar_1.8,c_fill,f_auto,q_auto,g_faces,w_300',
            medium: 'ar_2.23,c_fill,f_auto,q_auto,g_faces,w_964',
            large: 'ar_1.05,c_fill,f_auto,q_auto,g_faces,h_650',
            extraLarge: 'ar_1.4,c_fill,f_auto,q_auto,g_faces,h_650',
          }}
        />

        <WillOptions
          partnerName="Macmillan"
          telephoneWillsEnabled={areTelephoneWillsEnabled}
        />

        <Wrapper padding={['XL', 0, 'XXL']} paddingFromL={[96, 0, 'XXL']}>
          <GiftStories
            stories={[
              {
                title: 'Help nurses like Sandra to support people like Jerry',
                description:
                  'Our specialist Macmillan nurse Sandra was always on hand and we still attend a support group she started 13 years ago. I was in and out of hospital with infections from the osteoradionecrosis and could always ring or email our nurse to get seen in clinic or get advice.',
                source: 'Jerry, diagnosed with mouth cancer in 2014',
                image:
                  'com-assets/greylabel-landing-page-assets/macmillan/case-study-1',
              },
              {
                title: 'Help people like Naveed get financial support',
                description:
                  'I had to apply for Universal Credit but they didn’t believe that I was waiting for treatment. I appealed through Macmillan, via the Macmillan Support Line. They gave me their time and straightaway said they’d do it for me.',
                source: 'Naveed, diagnosed with a rare heart cancer in 2016',
                image:
                  'com-assets/greylabel-landing-page-assets/macmillan/case-study-2',
                imageTransformationParams: {
                  large: 'ar_1:1,c_auto,f_auto,q_auto,g_auto,r_max',
                },
              },
            ]}
            title="Over a third of Macmillan’s vital services are funded by gifts in wills"
          />
        </Wrapper>

        <GiftSlider
          caption="This calculation uses an average estate value of £505,000"
          partner="Macmillan"
          config={[
            {
              value: 0,
              label: '5%',
              text: '5% could fund a Macmillan nurse for over 4 months helping people living with cancer and their families receive essential medical, practical and emotional support.',
              image:
                'com-assets/greylabel-landing-page-assets/macmillan/gift-5',
            },
            {
              value: 25,
              label: '10%',
              text: '10% could support the Macmillan Welfare Rights Team for one week, helping people affected by cancer unlock on average £1.3million in unclaimed benefits and ease financial worries when it matters most.',
              image:
                'com-assets/greylabel-landing-page-assets/macmillan/gift-10',
            },
            {
              value: 50,
              label: '25%',
              text: '25% could fund the Macmillan Cancer Information Nurse Specialists for over a week, offering over 1,000 people affected by cancer specialist clinical advice and a caring, knowledgeable voice at the end of the phone.',
              image:
                'com-assets/greylabel-landing-page-assets/macmillan/gift-25',
            },
            {
              value: 75,
              label: '50%',
              text: '50% could help run Macmillan’s Online Community forum for a whole year, giving half a million people affected by cancer in the UK a safe space to share, connect and find comfort – day or night.',
              image:
                'com-assets/greylabel-landing-page-assets/macmillan/gift-50',
            },
            {
              value: 100,
              label: '100%',
              text: '100% could fund the Macmillan Cancer Information and Support Team for over 3 months, enabling over 9,000 people to get answers, practical help, and a listening ear throughout their cancer journey.',
              image:
                'com-assets/greylabel-landing-page-assets/macmillan/gift-100',
            },
          ]}
        />

        <Wrapper container noTrim>
          <Trustpilot
            tag="macmillan"
            title="Farewill is trusted by Macmillan supporters"
          />
          <BreakpointSwitch
            breakAt={BREAKPOINT.M}
            aboveComponent={<Divider />}
          />
          <FAQ
            questions={faqSection.questions}
            imagePath="com-assets/greylabel-landing-page-assets/macmillan/faq"
          />
          <BreakpointSwitch
            breakAt={BREAKPOINT.M}
            aboveComponent={<Divider />}
          />
          <Pledges
            title="Over £8 million pledged to Macmillan through Farewill in 2025"
            description="Help us reach the people living with cancer when they need us the most with a gift in your will to Macmillan."
            imagePath="fl_sanitize/cobranding-partner-logos/macmillan/macmillan-logo"
          />
        </Wrapper>

        <GetStarted
          partnerCampaignReferral
          telephoneWillsEnabled={areTelephoneWillsEnabled}
          title="Ready to write your will?"
        />
      </Wrapper>
    </DefaultLayout>
  )
}

export const query = graphql`
  query MacmillanGreyLabelPartnerLandingPage($slug: String!) {
    page: contentfulPartnerCampaignPage(slug: { eq: $slug }) {
      utmCampaign
      utmMedium
      heroTitle
      heroSubtitle {
        heroSubtitle
      }
      heroCtaText
      partner {
        partnerId
        telephoneWillsEnabled
        utmSource
        utmChannel
      }
      telephoneWillsEnabled
    }
    faqSection: contentfulFaqSection(
      contentful_id: { eq: "1aAywyoWcukAAEqsGZvkhC" }
    ) {
      ...FaqSectionFields
    }
  }
`

export default MacmillanGreyLabelLandingPage
